import { Delete, MoreVert } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { alpha, Box, Button, ClickAwayListener, Container, IconButton, Paper, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../../../context/Theme/ThemeContext";
import { getImagePath } from "../../../../util/envFn";
import ConfirmationDialog from "../../../ConfirmationDialog/ConfirmationDialog";
import PopupMenu from "../../../PopupMenu/PopupMenu";
import { useTodos } from "../../context/TodoContext/TodoContext";
import { Workspace as WorkspaceType } from "../../types/types";
import { useLogin } from "../../../../context/Login/LoginContext";

const Workspace = () => {
    const { isLoggedIn } = useLogin();
    const { workspacesLocal, workspacesServer, addWorkspace } = useTodos();

    const localCreate = () => addWorkspace(true);
    const serverCreate = () => addWorkspace(false);

    return (
        <Stack width="100%" overflow="auto">
            <Container maxWidth="lg" sx={{ py: 3, }}>
                <WorkspacesArea title="Local Workspaces" workspaces={workspacesLocal} create={localCreate} />
                {isLoggedIn && (
                    <WorkspacesArea title="Server Workspaces" workspaces={workspacesServer} create={serverCreate} />
                )}
            </Container>
        </Stack>
    );
}

const WorkspacesArea = ({ title, workspaces, create }: { title: string, workspaces: WorkspaceType[], create: () => void }) => {
    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2
            }}>
                {workspaces.map((workspace) => (
                    <WorkspaceCard
                        key={workspace.id}
                        workspace={workspace}
                    />
                ))}

                <Paper
                    onClick={create}
                    sx={{
                        // height: '100%',
                        height: 'auto',
                        minHeight: 196,
                        px: { xs: "12px", sm: "16px" },
                        maxWidth: { xs: 350, md: 500 },
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                        }
                    }}
                >
                    <Typography
                        variant="h4"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        +
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                    >
                        Create New Workspace
                    </Typography>
                </Paper>
            </Box>
        </Box>
    )
}


interface WorkspaceCardProps {
    workspace?: WorkspaceType;
}

const WorkspaceCard = ({ workspace }: WorkspaceCardProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isDarkMode } = useCustomTheme();
    const { updateWorkspace, deleteWorkspace } = useTodos();
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(workspace?.title || '');
    const [editedDescription, setEditedDescription] = useState(workspace?.description || '');
    const popupMenuAnchorEl = useRef<HTMLDivElement>(null);
    const [popupMenuOpen, setPopupMenuOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPopupMenuOpen(false);
        setIsEditing(true);
    };

    const handleSave = () => {
        if (workspace?.id) {
            updateWorkspace(workspace.id, {
                title: editedTitle,
                description: editedDescription
            });
        }
        setIsEditing(false);
    };

    const handleCardClick = () => {
        if (!isEditing && workspace?.id) {
            navigate(`/todo/${workspace.id}`);
        }
    };

    const openPopupMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (isEditing) return;
        setPopupMenuOpen(true);
    };

    const handleDeleteWorkspace = async () => {
        const isDeleted = await deleteWorkspace(workspace?.id || '');
        if (isDeleted)
            enqueueSnackbar(`Deleted "${workspace?.title}"`, { variant: 'success', preventDuplicate: false });
        setConfirmDeleteOpen(false);
    };

    return (
        <Paper
            onClick={handleCardClick}
            onContextMenu={openPopupMenu}
            sx={{
                p: { xs: "12px", sm: "16px" },
                maxWidth: { xs: 350, md: 500 },
                cursor: isEditing ? 'default' : 'pointer',
                height: 'auto',
                '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                },
            }}
        >
            <Box
                sx={{
                    height: {
                        xs: 100,
                        sm: 120,
                        md: 140
                    },
                    mb: {
                        xs: 1,
                        sm: 1.5,
                        md: 2
                    },
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: theme => theme.palette.background.default
                }}
            >
                <Box
                    component="img"
                    src={workspace?.image ?? isDarkMode ?
                        getImagePath("template/todo_default_light.png") :
                        getImagePath("template/todo_default.png")}
                    alt={`${workspace?.title} Image`}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            </Box>
            <Stack
                ref={popupMenuAnchorEl}
                sx={{ position: 'relative', minHeight: '80px', userSelect: 'none' }}
                gap={1}
            >
                {!isEditing ? (
                    <>
                        <Typography
                            variant={isMobile ? "subtitle1" : "body1"}
                            gutterBottom
                            sx={{
                                fontSize: {
                                    xs: '0.875rem',
                                    sm: '1rem'
                                },
                                pr: 4
                            }}
                        >
                            {workspace?.title}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={openPopupMenu}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0
                            }}
                        >
                            <MoreVert fontSize="small" />
                        </IconButton>
                        <Typography
                            variant={isMobile ? "caption" : "body2"}
                            color="text.secondary"
                            sx={{
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {workspace?.description?.trim() ? workspace.description : "No description"}
                        </Typography>
                    </>
                ) : (
                    <ClickAwayListener onClickAway={handleSave}>
                        <Stack onClick={e => e.stopPropagation()} gap={1}>
                            <TextField
                                fullWidth
                                size="small"
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                placeholder="Add description"
                                multiline
                                minRows={2}
                                onKeyDown={(e) => e.key === 'Enter' && e.shiftKey && handleSave()}
                            />
                            <Stack direction="row" justifyContent="flex-end" gap={1} mt={1}>
                                <Button variant="outlined" onClick={() => setIsEditing(false)}>Cancel</Button>
                                <Button variant="contained" onClick={handleSave}>Save</Button>
                            </Stack>
                        </Stack>
                    </ClickAwayListener>
                )}
            </Stack >
            <PopupMenu
                anchorEl={popupMenuAnchorEl.current}
                onClose={() => setPopupMenuOpen(false)}
                open={popupMenuOpen}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                options={[
                    {
                        label: "Edit",
                        icon: <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
                        onClick: handleEdit
                    },
                    {
                        label: "Delete",
                        icon: <Delete fontSize="small" sx={{ color: 'text.secondary' }} />,
                        onClick: () => setConfirmDeleteOpen(true)
                    }
                ]}
            />
            <ConfirmationDialog
                open={confirmDeleteOpen}
                severity="error"
                title="Delete Workspace"
                message={`Are you sure you want to delete "${workspace?.title}" ? This action cannot be undone.`}
                onConfirm={handleDeleteWorkspace}
                onCancel={() => setConfirmDeleteOpen(false)}
            />
        </Paper>
    );
};

export default Workspace;